import { mapBackendToFormFields } from './citationFieldMapper';

export const LOCATOR_TYPES = {
	PAGE: 'page',
	TIMESTAMP: 'timestamp',
	PARAGRAPH_OR_SECTION: 'paragraph_or_section',
	SLIDE: 'slide',
	FIGURE: 'figure',
	NONE: 'none'
};

function formFieldsFromCitation(citation) {
	if (!citation) return null;
	try {
		return mapBackendToFormFields(citation);
	} catch {
		return null;
	}
}

function formatSectionHeadingLocator(raw) {
	let t = String(raw || '')
		.trim()
		.replace(/^["']|["']$/g, '')
		.replace(/\s+section\s*$/i, '')
		.trim();
	if (!t) return '';
	return `"${t}" section`;
}

/**
 * Citation Locator Helper
 * Determines appropriate locator types for different citation sources and formats user input
 * Based on APA 7th Edition guidelines for in-text citation locators
 */

/**
 * Get locator type based on citation's mainCategory and subcategory
 * @param {string} mainCategory - The main category from backend
 * @param {string} subcategory - The subcategory from backend
 * @returns {string} - Locator type from LOCATOR_TYPES
 */
export function getLocatorTypeFromCitation(mainCategory, subcategory, citation = null) {
	// Books & Chapters - Use PAGE
	if (mainCategory === 'Books & Chapters' || mainCategory === 'Book & Chapter') {
		return LOCATOR_TYPES.PAGE;
	}

	// Periodicals — journal articles: pages; magazine/newspaper/preprint online: paragraph locators (APA)
	if (mainCategory === 'Periodicals') {
		const printVersion = citation?.publishedOnline === 'No';
		if (
			subcategory === 'Magazine Article' ||
			subcategory === 'Magazine or Newspaper Article' ||
			subcategory === 'Newspaper Article' ||
			subcategory === 'Preprint'
		) {
			return printVersion ? LOCATOR_TYPES.PAGE : LOCATOR_TYPES.PARAGRAPH_OR_SECTION;
		}
		return LOCATOR_TYPES.PAGE;
	}

	// Web Content - Use PARAGRAPH or SECTION (webpage, blog)
	if (mainCategory === 'Web Content') {
		return LOCATOR_TYPES.PARAGRAPH_OR_SECTION;
	}

	// Reports & Government Docs — institutional reports support page, paragraph, and section locators
	if (mainCategory === 'Reports & Government Docs') {
		if (subcategory === 'Report' || subcategory === 'Institutional Report') {
			return LOCATOR_TYPES.PARAGRAPH_OR_SECTION;
		}
		if (subcategory === 'Press Release') {
			return LOCATOR_TYPES.PARAGRAPH_OR_SECTION;
		}
		return LOCATOR_TYPES.PAGE;
	}

	// Data & Software — datasets often lack page numbers (APA 7 web-style locators)
	if (mainCategory === 'Data & Software') {
		if (subcategory === 'Dataset or Software' || subcategory === 'Data Set' || subcategory === 'Dataset') {
			return LOCATOR_TYPES.PARAGRAPH_OR_SECTION;
		}
		return LOCATOR_TYPES.NONE;
	}

	// Multimedia — infographics use figure/section locators, not timestamps
	if (mainCategory === 'Multimedia') {
		if (
			subcategory === 'Infographic/Chart/Graph' ||
			subcategory === 'Infographic' ||
			subcategory === 'Chart' ||
			subcategory === 'Graph'
		) {
			return LOCATOR_TYPES.FIGURE;
		}
		return LOCATOR_TYPES.TIMESTAMP;
	}

	// Conferences & Presentations — course handouts: slides or timestamps for lecture recordings
	if (mainCategory === 'Conferences & Presentations' || mainCategory === 'Course & Education') {
		if (subcategory === 'Course Handout') {
			const fmt =
				citation?.format ||
				citation?.descriptor ||
				(formFieldsFromCitation(citation)?.format) ||
				'';
			if (/lecture recording/i.test(String(fmt))) {
				return LOCATOR_TYPES.TIMESTAMP;
			}
			return LOCATOR_TYPES.SLIDE;
		}
		return LOCATOR_TYPES.SLIDE;
	}

	// Figures, Infographics & Charts - Use FIGURE
	if (mainCategory === 'Figures, Infographics & Charts') {
		return LOCATOR_TYPES.FIGURE;
	}

	// Social & Personal Communication — X/Twitter etc.: no page/paragraph locators (SOW)
	if (mainCategory === 'Social & Personal Communication') {
		return LOCATOR_TYPES.NONE;
	}

	// Default to PAGE for academic sources
	return LOCATOR_TYPES.PAGE;
}

/**
 * Format user input based on locator type (smart auto-formatting!)
 * Implements APA 7th edition formatting rules
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @param {string} userInput - Raw user input (e.g., "23", "4:15", "Introduction")
 * @returns {string} - Formatted locator (e.g., "p. 23", "04:15", "Introduction section")
 */
export function formatLocatorValue(locatorType, userInput) {
	if (!userInput || !userInput.trim()) return '';

	const value = userInput.trim().replace(/\.$/, '');

	switch (locatorType) {
		case LOCATOR_TYPES.PAGE: {
			// Pass through paragraph/section locators when user enters them on a page-type field
			if (/^paras?\.?\s/i.test(value)) {
				return value.replace(/^para\.?\s*/i, (m) => (m.toLowerCase().includes('paras') ? 'paras. ' : 'para. ')).trim();
			}
			if (!/^[\d\s,–-]+$/.test(value) && !/^[A-Za-z]\d+$/i.test(value)) {
				return formatLocatorValue(LOCATOR_TYPES.PARAGRAPH_OR_SECTION, userInput);
			}

			let formattedValue = value.replace(/-/g, '–');
			formattedValue = formattedValue.replace(/,\s*/g, ', ');

			const isRange = formattedValue.includes('–') || formattedValue.includes(',');
			return isRange ? `pp. ${formattedValue}` : `p. ${formattedValue}`;
		}

		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION: {
			const isNumberOrRange = /^[\d\s,–-]+$/.test(value);

			if (isNumberOrRange) {
				const formattedValue = value.replace(/-/g, '–');
				const isRange = formattedValue.includes('–') || formattedValue.includes(',');
				return isRange ? `paras. ${formattedValue}` : `para. ${formattedValue}`;
			}
			if (value.includes(',')) {
				const parts = value.split(',').map((p) => p.trim().replace(/-/g, '–')).filter(Boolean);
				if (
					parts.length > 0 &&
					parts.every((p) => /^[A-Za-z]\d+$/i.test(p) || /^[\d\s,–-]+$/.test(p))
				) {
					return parts.length > 1 ? `paras. ${parts.join(', ')}` : `para. ${parts[0]}`;
				}
			}
			if (/^[A-Za-z]\d+$/i.test(value.trim())) {
				return `para. ${value.trim()}`;
			}
			const t = value.trim();
			if (/\bsection\s*$/i.test(t)) {
				return t;
			}
			if (/^figure\s+\d+/i.test(t) || /^table\s+\d+/i.test(t)) {
				return t;
			}
			return formatSectionHeadingLocator(t);
		}

		case LOCATOR_TYPES.TIMESTAMP: {
			const parts = value.split(':').map((p) => p.trim());
			if (parts.length === 2) {
				const [min, sec] = parts.map((p) => p.padStart(2, '0'));
				return `${min}:${sec}`;
			}
			if (parts.length === 3) {
				const hRaw = parseInt(parts[0], 10);
				const hour = Number.isNaN(hRaw) ? parts[0] : String(hRaw);
				const min = parts[1].padStart(2, '0');
				const sec = parts[2].padStart(2, '0');
				return `${hour}:${min}:${sec}`;
			}
			return value;
		}

		case LOCATOR_TYPES.SLIDE: {
			const formattedValue = value.replace(/-/g, '–');
			const isRange = formattedValue.includes('–') || formattedValue.includes(',');
			return isRange ? `Slides ${formattedValue}` : `Slide ${formattedValue}`;
		}

		case LOCATOR_TYPES.FIGURE: {
			if (/^\d+$/.test(value)) {
				return `Figure ${value}`;
			}
			if (/^figure\s+\d+/i.test(value)) {
				return value.trim();
			}
			return formatSectionHeadingLocator(value);
		}

		default:
			return value;
	}
}

/**
 * Get label for the locator input field
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Label text for UI
 */
export function getLocatorLabel(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return 'Locator (page, paragraph, section, etc.)';
		case LOCATOR_TYPES.TIMESTAMP:
			return 'Locator (optional)';
		case LOCATOR_TYPES.FIGURE:
			return 'Figure reference';
		default:
			return 'Locator';
	}
}

/**
 * Get placeholder text for the locator input
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Placeholder text
 */
export function getLocatorPlaceholder(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return '23, 45-46, para. 4, or Results';
		case LOCATOR_TYPES.TIMESTAMP:
			return '2:15 or 7:22:05';
		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return '4, para. 4, or Results';
		case LOCATOR_TYPES.SLIDE:
			return '7 or 10-12';
		case LOCATOR_TYPES.FIGURE:
			return 'Figure 2';
		default:
			return '';
	}
}

/**
 * Get tooltip/help text for the locator field
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @returns {string} - Help text
 */
export function getLocatorHelpText(locatorType) {
	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			return 'Enter the page number(s) where the quoted or paraphrased text appears. Single page: 10 | Range: 10-15 | Multiple ranges/pages: 10-15, 22, 30-35 (use commas to separate).';
		case LOCATOR_TYPES.TIMESTAMP:
			return 'Enter a timestamp for audio or video (e.g., 15:30), or a slide number when citing presentation materials.';
		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return 'Type a page number, paragraph number, or section heading. Section headings are formatted with quotation marks (e.g., Results → "Results" section).';
		case LOCATOR_TYPES.SLIDE:
			return 'Enter the slide number(s) when citing a presentation or slideshow (e.g., 4 or 10-12).';
		case LOCATOR_TYPES.FIGURE:
			return 'Locators are optional for infographics. For general citations, leave blank. To cite a specific part, enter a figure number (e.g., Figure 2) or section heading (e.g., Conclusion).';
		default:
			return 'Enter the specific location where the quote appears';
	}
}

/**
 * Validate locator input based on type
 * @param {string} locatorType - Type from LOCATOR_TYPES
 * @param {string} userInput - User's input
 * @returns {object} - { isValid: boolean, error: string }
 */
export function validateLocatorInput(locatorType, userInput) {
	if (!userInput || !userInput.trim()) {
		return { isValid: true, error: '' }; // Empty is okay (locator is optional)
	}

	const value = userInput.trim();

	const invalidLabelPattern = /^(pages?|p\.?|pp\.?|paras?|paragraphs?|chapters?|chaps?|ch\.?|vols?|slides?|figures?|figs?)\b[.\s]*/i;

	if (invalidLabelPattern.test(value)) {
		if (locatorType === LOCATOR_TYPES.FIGURE) {
			return { isValid: true, error: '' };
		}

		if (locatorType === LOCATOR_TYPES.PAGE && /^paras?\.?/i.test(value)) {
			// allowed
		} else {
			switch (locatorType) {
				case LOCATOR_TYPES.PAGE:
					return { isValid: false, error: "Please enter only the page number(s) (e.g., 7 or 15-16). We automatically add 'p.' or 'pp.'." };
				case LOCATOR_TYPES.SLIDE:
					return { isValid: false, error: "Please enter only the slide number(s) (e.g., 4). We automatically add 'Slide'." };
				case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
					if (/^(paras?|paragraphs?)\b/i.test(value)) {
						return { isValid: false, error: "Please enter only the paragraph number (e.g., 4). We automatically add 'para.'." };
					}
					return { isValid: false, error: "Please enter the paragraph number (e.g., 4) or section title (e.g., Results). Avoid prefixes like 'page' or 'chapter'." };
				default:
					return { isValid: false, error: "Please enter only the value. Do not include labels like 'page' or 'para'." };
			}
		}
	}

	switch (locatorType) {
		case LOCATOR_TYPES.PAGE:
			if (!/^[\d\s,–-]+$/.test(value) && !/^[A-Za-z]\d+$/i.test(value) && !/^paras?\.?\s*\d/i.test(value)) {
				if (/[a-zA-Z]/.test(value)) {
					return { isValid: true, error: '' };
				}
				return {
					isValid: false,
					error: 'Please enter a valid page number, range (e.g., 15-16), or list (e.g., 10, 15).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.SLIDE:
			if (!/^[\d\s,–-]+$/.test(value)) {
				return {
					isValid: false,
					error: 'Please enter a valid slide number or range (e.g., 4 or 4-5).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.TIMESTAMP:
			if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(value.replace(/\s+/g, ''))) {
				return {
					isValid: false,
					error: 'Use mm:ss or h:mm:ss (e.g., 02:15 or 7:10:25).'
				};
			}
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.PARAGRAPH_OR_SECTION:
			return { isValid: true, error: '' };

		case LOCATOR_TYPES.FIGURE:
			return { isValid: true, error: '' };

		default:
			return { isValid: true, error: '' };
	}
}
